import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, forwardRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, tap, noop } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { SharedModule } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { AngularImporterModule } from 'src/app/importers/angular-importer.module';
import { PrimeNGImporterModule } from 'src/app/importers/prime-ng-importer.module';

@Component({
  selector: 'app-textarea',
  standalone: true,
  templateUrl: './textarea.component.html',
  styleUrl: './textarea.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, AngularImporterModule, PrimeNGImporterModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true,
    },
  ],
})
export class TextareaComponent implements ControlValueAccessor, OnInit {
  @Input() ngClass: any;
  @Input() charactersPerLine: number = 9999;
  @Input() maxLines: number = 9999;
  @Input() rows: number = 3;
  @Input() maxlength: number = 9999;
  @Input() isResize: boolean = false;
  @Input() isUpper: boolean = false;

  // @ViewChild('textArea') textArea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('textArea') set content(content: ElementRef<HTMLTextAreaElement>) {
    if(content) {
      content.nativeElement.oninput = (e) => {
        let charactersPerLine = this.charactersPerLine;
        let maxLines = this.maxLines;
        var textInput = content.nativeElement.value;//1a: READ VALUE
        if (this.isUpper) {
          textInput = textInput.toUpperCase();
        }
        var inputAsRows = textInput.split("\n");// create array from input => each element contains one row of the textarea
        var inputAsOneLine = textInput.replace(/(\r\n\t|\n|\r\t)/gm, "");//remove all line-breaks
        var cursorPositionOnInput = content.nativeElement.selectionStart;//2a: READ CURSOR POSITION
        var cursorOffsetAfterOutput = 0;//set default value for cursor offset. cursor offset is needed when re-posiotioning the cursor after WRITE DATA
  
        var totalRows = inputAsRows.length; //don't put inputAsRows.length in the for statement, as the array is growing in the loop which results in an infinite loop
        var row;
        var lineBreakCount = 0;
        var characterCount = 0;
        for (row = 0; row < totalRows; ++row) {
          if (inputAsRows[row].length > charactersPerLine) { //1b DETECT IF TEXT PER LINE IS TOO LONG 
            if (inputAsRows[row + 1] === undefined) {
              inputAsRows[row + 1] = "";// the row did not exist
              totalRows++;
            }
            //1c PUSH OVERFLOWING TEXT: move text that is too long for this row to the next row:
            inputAsRows[row + 1] = inputAsRows[row].substring(charactersPerLine) + inputAsRows[row + 1];
            inputAsRows[row] = inputAsRows[row].substring(0, charactersPerLine);
            //determine, if cursor was at the end of the line that got a line-break:
            var newOutput = inputAsRows.join("\n");
            if (newOutput.substr(cursorPositionOnInput - 1, 1) == "\n") {
              cursorOffsetAfterOutput = 1;
            }
          }
        }
  
        if (inputAsRows.length <= maxLines && inputAsOneLine.length <= (maxLines * charactersPerLine)) {//data is within max number of rows and max total digits
          this.textOutput = inputAsRows.join("\n");
          if (this.isResize) {
            content.nativeElement.rows = inputAsRows.length;//resize textarea
          }
          if (this.onPaste) {
            cursorOffsetAfterOutput = this.cursorOffsetOnPaste(textInput, cursorPositionOnInput, totalRows)
          }
        }
        else //data would be too long 
        {
          cursorOffsetAfterOutput = -1;
        }
        content.nativeElement.value = this.textOutput;//1d: WRITE VALUE
        content.nativeElement.selectionStart = cursorPositionOnInput + cursorOffsetAfterOutput; //2b: POSITION CURSOR
        content.nativeElement.selectionEnd = cursorPositionOnInput + cursorOffsetAfterOutput; //set a single cursor, not a selection
        this.onPaste = false;
        this.formControl.setValue(this.textOutput);
        this.value = this.textOutput;
      }
    }
  }

  constructor(private cd: ChangeDetectorRef) {}

  formControl: FormControl = new FormControl<string>('');

  destroyRef: DestroyRef = inject(DestroyRef);

  textOutput = "";
  onPaste = false;
  cursorOffsetAfterOutput = 0;

  onChange: (value: string) => void = noop;
  onTouch: () => void = noop;

  disabled = false;
  value = '';

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable() : this.formControl.enable();
    this.disabled = isDisabled;
  }

  writeValue(value: string): void {
    let newValue = '';
    if (value) {
      let values = value.match(new RegExp('.{1,' + this.charactersPerLine + '}', 'g')) || [];
      let newValues: string[] = [];
      values.forEach((line) => {
        if (line.length > this.charactersPerLine) {
          newValues.push(line.substring(0, this.charactersPerLine));
          newValues.push(line.substring(this.charactersPerLine));
        }
        else {
          newValues.push(line);
        }
      });
      newValue = newValues.join('\n');
    }
    this.formControl.setValue(newValue);
    this.value = newValue;
    this.textOutput = newValue;
  }

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        debounceTime(200),
        tap(value => this.onChange(value)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  countLineBreaks(string: string, lengthFromStart: number) {
    var left = string.substr(0, lengthFromStart);
    var countOfLinebreaks = (left.split("\n")).length;
    return countOfLinebreaks;
  }

  handlePaste() {
    //some improvements when pasting content can still be made (particularly on the cursor position)
    this.onPaste = true;
  }

  cursorOffsetOnPaste(textInput: string, cursorPositionOnInput: number, totalRows: number) {
    //offset the cursor by 1 for each added line break:
    var countOld = this.countLineBreaks(textInput, cursorPositionOnInput);
    var countNew = this.countLineBreaks(this.textOutput, cursorPositionOnInput + totalRows);
    this.cursorOffsetAfterOutput = countNew - countOld;
    return this.cursorOffsetAfterOutput;
  }

  onModelChange() {
    // this.writeValue(this.value);
    this.formControl.patchValue(this.value);
  }
}